import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, MenuItem, Alert, CircularProgress, IconButton
} from '@mui/material';
import { Home, Print, PieChart, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DimensionAnalysis = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [dims, setDims]       = useState([]);
  const [dimension, setDim]   = useState('');
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    axios.get(`${API}/api/finance/dimensions`, { headers: hdrs() })
      .then(r => { setDims(r.data || []); if (r.data?.length) setDim(r.data[0].code); })
      .catch(() => {});
  }, []);

  const run = useCallback(async () => {
    if (!dimension) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/finance/gl-by-dimension`, { headers: hdrs(), params: { dimension, from, to } });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to run analysis'); }
    finally { setLoading(false); }
  }, [dimension, from, to]);

  const exportExcel = () => {
    if (!data) return;
    const rows = [
      [company?.name || 'GL BY DIMENSION'],
      ['GL BY DIMENSION'],
      [`Dimension: ${data.dimension}`],
      [`Period: ${from || 'Start'} to ${to || 'End'}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      [`${data.dimension} Value`, 'Name', 'Debit', 'Credit', 'Net'],
      ...data.rows.map(r => [r.value, r.name, r.debit, r.credit, r.net]),
      [],
      ['Totals', '', data.totals.debit, data.totals.credit, data.totals.net],
    ];
    downloadCsv(rows, `gl_by_${data.dimension}.csv`);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } }`}</style>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} className="no-print">
            <Box display="flex" alignItems="center">
              <PieChart sx={{ mr: 1, color: '#6a1b9a' }} />
              <Typography variant="h5" sx={{ color: '#6a1b9a' }}>GL by Dimension</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
                sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
              <IconButton onClick={() => window.print()} title="Print" disabled={!data}
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
              <IconButton onClick={() => navigate('/finance')} title="Finance Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Net GL movement grouped by a dimension's values. Entries without a value fall under "(none)".
          </Typography>

          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2} className="no-print">
            <TextField select label="Dimension" value={dimension} size="small" sx={{ minWidth: 180 }}
              onChange={e => setDim(e.target.value)}>
              {dims.map(d => <MenuItem key={d.code} value={d.code}>{d.code} — {d.name}</MenuItem>)}
            </TextField>
            <TextField type="date" label="From" value={from} size="small" onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField type="date" label="To" value={to} size="small" onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            <IconButton onClick={run} disabled={loading || !dimension} color="primary" sx={{ bgcolor: '#6a1b9a15' }}>
              {loading ? <CircularProgress size={20} /> : <PieChart />}
            </IconButton>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {data && (
            data.rows.length === 0 ? <Typography color="text.secondary">No GL entries in this period.</Typography> : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{data.dimension} Value</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell align="right"><strong>Debit</strong></TableCell>
                      <TableCell align="right"><strong>Credit</strong></TableCell>
                      <TableCell align="right"><strong>Net</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.rows.map(r => (
                      <TableRow key={r.value} hover>
                        <TableCell><strong>{r.value}</strong></TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell align="right">{fmt(r.debit)}</TableCell>
                        <TableCell align="right">{fmt(r.credit)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: r.net >= 0 ? 'success.main' : 'error.main' }}>{fmt(r.net)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 800 }}>Totals</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(data.totals.debit)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(data.totals.credit)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>{sym()} {fmt(data.totals.net)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DimensionAnalysis;
